import React, { useEffect, useRef, useState } from 'react';
import { MdSettings, MdLogout } from 'react-icons/md';
import { FiSettings } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useStateContext } from '../contexts/ContextProvider';
import { useAuth } from '../contexts/AuthContext';
import { useXMPP, type XMPPStatus } from '../contexts/XMPPContext';

const UserProfile = () => {
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const {
    setIsClicked,
    initialState,
  } = useStateContext();
  const { user, logout } = useAuth();
  const { status: xmppStatus, setStatus: setXmppStatus } = useXMPP();

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsClicked(initialState);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsClicked, initialState]);

  const handleClose = () => {
    setIsClicked(initialState);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    // No need to navigate - ProtectedRoute will automatically show Login component
  };

  const handlePreferences = () => {
    handleClose();
    navigate('/preferences');
  };

  const handleSettings = () => {
    handleClose();
    // Navigate to settings page when implemented
    // navigate('/settings');
  };

  return (
    <div 
      ref={profileRef}
      className="nav-items bg-base-100 border border-base-300 right-1 top-16 px-4 py-4 box-border absolute w-64 rounded-xl shadow-lg z-50"
    >
      <div className="mb-4">
        <p className="text-lg font-semibold">User Menu</p>
      </div>
      
      {/* User Info */}
      <div className="mb-4 pb-4 border-b border-base-300">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
            <span className="text-lg font-medium text-white">
              {user?.initials
                ? user.initials.toUpperCase()
                : user?.first_name && user?.last_name
                ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
                : (user as any)?.username?.substring(0, 2).toUpperCase() || 'AU'}
            </span>
          </div>
          <div>
            <p className="font-semibold">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : (user as any)?.username || 'User'}
            </p>
            <p className="text-xs text-base-content/60">{user?.email || (user as any)?.username}</p>
          </div>
        </div>
      </div>

      {/* XMPP Status Selector */}
      <div className="mb-4 pb-4 border-b border-base-300">
        <p className="text-sm font-medium mb-2">Chat Status</p>
        <div className="space-y-2">
          <button
            onClick={() => setXmppStatus('online')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
              xmppStatus === 'online' ? 'bg-success/10 text-success' : 'hover:bg-base-200'
            }`}
          >
            <div className="w-3 h-3 rounded-full bg-success"></div>
            <span>Online</span>
          </button>
          
          <button
            onClick={() => setXmppStatus('away')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
              xmppStatus === 'away' ? 'bg-warning/10 text-warning' : 'hover:bg-base-200'
            }`}
          >
            <div className="w-3 h-3 rounded-full bg-warning"></div>
            <span>Away</span>
          </button>
          
          <button
            onClick={() => setXmppStatus('dnd')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
              xmppStatus === 'dnd' ? 'bg-error/10 text-error' : 'hover:bg-base-200'
            }`}
          >
            <div className="w-3 h-3 rounded-full bg-error"></div>
            <span>Do Not Disturb</span>
          </button>
          
          <button
            onClick={() => setXmppStatus('offline')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
              xmppStatus === 'offline' ? 'bg-base-300/50' : 'hover:bg-base-200'
            }`}
          >
            <div className="w-3 h-3 rounded-full bg-base-300"></div>
            <span>Offline</span>
          </button>
        </div>
      </div>

      {/* Menu Items */}
      <div className="space-y-1">
        <button
          onClick={handlePreferences}
          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-base-200 rounded-lg transition-colors"
        >
          <FiSettings className="text-lg" />
          <span>Preferences</span>
        </button>
        
        <button
          onClick={handleSettings}
          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-base-200 rounded-lg transition-colors"
        >
          <MdSettings className="text-lg" />
          <span>Settings</span>
        </button>
        
        <div className="border-t border-base-300 my-2"></div>
        
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-error hover:text-error-content rounded-lg transition-colors text-error"
        >
          <MdLogout className="text-lg" />
          <span>Log out</span>
        </button>
      </div>
    </div>
  )
}

export default UserProfile